import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

type Flags = Record<string, boolean>

type FlagsContextValue = {
  flags: Flags
  get: (key: string, def?: boolean) => boolean
  refresh: () => Promise<void>
  setLocal: (key: string, val: boolean) => void
}

const FlagsContext = createContext<FlagsContextValue | undefined>(undefined)

export const FlagsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [flags, setFlags] = useState<Flags>(() => {
    try { return JSON.parse(localStorage.getItem('veridoc:flags') || '{}') } catch { return {} }
  })

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/ui/flags')
      if (data && typeof data === 'object') {
        setFlags((prev) => ({ ...data, ...prev }))
      }
    } catch (error) {
      console.warn('Failed to load flags', error)
    }
  }, [])

  useEffect(() => { localStorage.setItem('veridoc:flags', JSON.stringify(flags)) }, [flags])

  const get = useCallback((key: string, def = false) => (key in flags ? !!flags[key] : def), [flags])
  const setLocal = useCallback((key: string, val: boolean) => setFlags(prev => ({ ...prev, [key]: val })), [])

  const value = useMemo(() => ({ flags, get, refresh, setLocal }), [flags, get, refresh, setLocal])
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
}

export function useFlags() {
  const ctx = useContext(FlagsContext)
  if (!ctx) throw new Error('useFlags must be used within FlagsProvider')
  return ctx
}
